import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { env } from "@/config/envvars";
import { POLL_IDLE_MS } from "@/lib/realtime";
import { useAuthStore } from "@/stores/authStore";
import { reconcileSignals } from "../market-signals/api/signalsCache";
import { brandUniversePendingFactProposalsKey } from "./useBrandUniversePendingFactProposals";
import type {
  AgentCatalogEntry,
  AgentRun,
  AgentRunStartResponse,
  RunAgentSequenceRequest,
  SequenceEvent,
  SequenceLiveState,
} from "@/types/agent.type";

const ACTIVE_STATUSES = new Set(["queued", "running"]);

export function useAvailableAgents() {
  return useQuery({
    queryKey: ["agentTypes"],
    queryFn: async () => {
      const res = await axiosInstance.get<{ agents: AgentCatalogEntry[] }>(
        "/agent/types",
      );
      return res.data.agents;
    },
  });
}

export function useAgentRunsByBrand(brandId: string | null) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["agentRuns", brandId],
    queryFn: async () => {
      const res = await axiosInstance.get<AgentRun[]>(
        `/agent/runs/brand/${brandId}`,
      );
      return res.data;
    },
    enabled: !!brandId,
    // 3s while a run is live; never fully off — runs started by crons,
    // schedules, MCP or teammates would otherwise be invisible until reload.
    refetchInterval: (query) => {
      const runs = query.state.data;
      return runs?.some((r) => ACTIVE_STATUSES.has(r.status)) ? 3000 : POLL_IDLE_MS;
    },
  });

  // A run finishing means its side effects just landed (signals,
  // universe facts, fact proposals). This poll is the one place that reliably
  // sees the transition, so it fans the freshness out — the closest thing this
  // stack has to a push event. Two completion signals: a previously-active run
  // left the active set, or a never-seen run arrived already terminal (started
  // AND finished inside one idle tick). The ref is brand-scoped so switching
  // brands resets tracking instead of reading the old brand's runs as done.
  const trackedRef = useRef<{
    brandId: string;
    activeIds: Set<string>;
    knownIds: Set<string>;
  } | null>(null);
  const runs = query.data;
  useEffect(() => {
    if (!brandId || !runs) return;
    const activeIds = new Set(
      runs.filter((r) => ACTIVE_STATUSES.has(r.status)).map((r) => r.id),
    );
    const knownIds = new Set(runs.map((r) => r.id));
    const prev = trackedRef.current;
    trackedRef.current = { brandId, activeIds, knownIds };
    if (!prev || prev.brandId !== brandId) return;
    const activeFinished = [...prev.activeIds].some((id) => !activeIds.has(id));
    const arrivedTerminal = runs.some(
      (r) => !ACTIVE_STATUSES.has(r.status) && !prev.knownIds.has(r.id),
    );
    if (!activeFinished && !arrivedTerminal) return;
    reconcileSignals(queryClient, brandId);
    queryClient.invalidateQueries({ queryKey: ["brandUniverseGraph", brandId] });
    queryClient.invalidateQueries({ queryKey: brandUniversePendingFactProposalsKey(brandId) });
  }, [brandId, runs, queryClient]);

  return query;
}

export function useStartAgentRun(brandId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentType,
      inputParams,
    }: {
      agentType: string;
      inputParams?: Record<string, unknown>;
    }) => {
      const res = await axiosInstance.post<AgentRunStartResponse>(
        `/agent/brand/${brandId}/run`,
        { agentType, inputParams },
      );
      return res.data;
    },
    onSuccess: () => {
      if (brandId) {
        queryClient.invalidateQueries({ queryKey: ["agentRuns", brandId] });
        reconcileSignals(queryClient, brandId);
      }
    },
  });
}

const INITIAL_SEQUENCE_STATE: SequenceLiveState = {
  phase: "idle",
  steps: [],
  iterations: 0,
  summary: null,
  errorMessage: null,
};

/** Fold one streamed event into the live sequence state. Pure — easy to reason about. */
function reduceSequenceEvent(
  prev: SequenceLiveState,
  event: SequenceEvent,
): SequenceLiveState {
  switch (event.type) {
    case "sequence_start":
      return {
        phase: "running",
        iterations: event.iterations,
        summary: null,
        errorMessage: null,
        steps: event.plan.map((p) => ({ ...p, status: "pending", error: null })),
      };
    case "run_start":
      return {
        ...prev,
        steps: prev.steps.map((s) =>
          s.index === event.index ? { ...s, status: "running" } : s,
        ),
      };
    case "run_complete":
      return {
        ...prev,
        steps: prev.steps.map((s) =>
          s.index === event.index
            ? { ...s, status: event.status, error: event.error }
            : s,
        ),
      };
    case "sequence_complete":
      return { ...prev, phase: "done", summary: event.summary };
    default:
      return prev;
  }
}

/**
 * Runs an agent sequence and streams progress over SSE. Uses `fetch` + a stream
 * reader (not EventSource) so we can POST the body and send the auth header, and
 * sets no request timeout — the run is bound only by the work itself.
 */
export function useRunAgentSequence(brandId: string | null) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<SequenceLiveState>(INITIAL_SEQUENCE_STATE);
  const runningRef = useRef(false);

  const reset = useCallback(() => setState(INITIAL_SEQUENCE_STATE), []);

  const start = useCallback(
    async (payload: RunAgentSequenceRequest) => {
      if (!brandId || runningRef.current) return;
      runningRef.current = true;
      setState({ ...INITIAL_SEQUENCE_STATE, phase: "running" });

      const token = useAuthStore.getState().token;

      try {
        const res = await fetch(
          `${env.BACKEND_URL}${env.API_POSTFIX}/agent/brand/${brandId}/run-sequence`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(payload),
          },
        );

        // Validation / auth errors come back as JSON before the stream opens.
        if (!res.ok || !res.body) {
          let message = "Could not start the sequence.";
          try {
            const data = (await res.json()) as { error?: string };
            if (data.error) message = data.error;
          } catch {
            /* non-JSON body — keep the default message */
          }
          setState((s) => ({ ...s, phase: "error", errorMessage: message }));
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let sep: number;
          while ((sep = buffer.indexOf("\n\n")) !== -1) {
            const frame = buffer.slice(0, sep).trim();
            buffer = buffer.slice(sep + 2);
            // Skip ": ping" heartbeat comments; only data frames carry events.
            if (!frame.startsWith("data:")) continue;
            const event = JSON.parse(
              frame.slice(frame.indexOf("data:") + 5).trim(),
            ) as SequenceEvent;
            setState((prev) => reduceSequenceEvent(prev, event));
          }
        }
      } catch (err) {
        setState((s) => ({
          ...s,
          phase: "error",
          errorMessage: err instanceof Error ? err.message : "Stream failed.",
        }));
      } finally {
        runningRef.current = false;
        queryClient.invalidateQueries({ queryKey: ["agentRuns", brandId] });
        reconcileSignals(queryClient, brandId);
      }
    },
    [brandId, queryClient],
  );

  return { start, reset, state, isRunning: state.phase === "running" };
}
